import React from 'react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';

type BadgeVariant =
  | 'default'
  | 'primary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'secondary'
  | 'outline'
  | 'purple'
  // Task status variants (modern naming)
  | 'running'
  | 'completed'
  | 'pending'
  | 'failed'
  | 'sleeping'
  // Task status variants (imperial naming)
  | 'executing'
  | 'accomplished'
  | 'queued'
  | 'malfunction'
  | 'standby';

interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  className?: string;
  dot?: boolean;
  pulse?: boolean;
}

export function Badge({
  children,
  variant = 'default',
  className,
  dot = false,
  pulse = false,
}: BadgeProps) {
  const { themeName } = useTheme();
  const isCommand = themeName === 'command';

  const baseStyles = cn(
    'inline-flex items-center gap-1.5',
    isCommand
      ? 'px-2 py-0.5 text-[10px] font-semibold font-mono uppercase tracking-wider border rounded'
      : 'px-2 py-0.5 text-xs font-medium rounded-full'
  );

  const variantStyles: Record<BadgeVariant, string> = isCommand
    ? {
        default: 'bg-surface-600 text-text-secondary border-surface-500',
        primary: 'bg-primary-500/20 text-primary-400 border-primary-500/50',
        success: 'bg-success-500/20 text-success-400 border-success-500/50',
        warning: 'bg-warning-500/20 text-warning-400 border-warning-500/50',
        danger: 'bg-danger-500/20 text-danger-400 border-danger-500/50',
        secondary: 'bg-surface-500/50 text-text-secondary border-surface-400',
        outline: 'bg-transparent text-text-secondary border-surface-500',
        purple: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
        // Modern status
        running: 'bg-primary-500/20 text-primary-400 border-primary-500/50 shadow-glow-sm',
        completed: 'bg-success-500/20 text-success-400 border-success-500/50',
        pending: 'bg-surface-500/30 text-text-muted border-surface-500',
        failed: 'bg-danger-500/20 text-danger-400 border-danger-500/50 shadow-glow-danger',
        sleeping: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
        // Imperial status (aliases)
        executing: 'bg-primary-500/20 text-primary-400 border-primary-500/50 shadow-glow-sm',
        accomplished: 'bg-success-500/20 text-success-400 border-success-500/50',
        queued: 'bg-surface-500/30 text-text-muted border-surface-500',
        malfunction: 'bg-danger-500/20 text-danger-400 border-danger-500/50 shadow-glow-danger',
        standby: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
      }
    : {
        default: 'bg-surface-600 text-text-secondary',
        primary: 'bg-primary-500/20 text-primary-400 border border-primary-500/30',
        success: 'bg-success-500/20 text-success-400 border border-success-500/30',
        warning: 'bg-warning-500/20 text-warning-400 border border-warning-500/30',
        danger: 'bg-danger-500/20 text-danger-400 border border-danger-500/30',
        secondary: 'bg-surface-600 text-text-muted',
        outline: 'bg-transparent text-text-secondary border border-surface-500',
        purple: 'bg-purple-500/20 text-purple-400 border border-purple-500/30',
        // Modern status
        running: 'bg-primary-500/20 text-primary-400 border border-primary-500/30',
        completed: 'bg-success-500/20 text-success-400 border border-success-500/30',
        pending: 'bg-surface-600 text-text-muted',
        failed: 'bg-danger-500/20 text-danger-400 border border-danger-500/30',
        sleeping: 'bg-purple-500/20 text-purple-400 border border-purple-500/30',
        // Imperial status (aliases)
        executing: 'bg-primary-500/20 text-primary-400 border border-primary-500/30',
        accomplished: 'bg-success-500/20 text-success-400 border border-success-500/30',
        queued: 'bg-surface-600 text-text-muted',
        malfunction: 'bg-danger-500/20 text-danger-400 border border-danger-500/30',
        standby: 'bg-purple-500/20 text-purple-400 border border-purple-500/30',
      };

  const dotColors: Record<BadgeVariant, string> = {
    default: 'bg-text-muted',
    primary: 'bg-primary-400',
    success: 'bg-success-500',
    warning: 'bg-warning-500',
    danger: 'bg-danger-500',
    secondary: 'bg-text-muted',
    outline: 'bg-text-muted',
    purple: 'bg-purple-400',
    running: 'bg-primary-400',
    completed: 'bg-success-500',
    pending: 'bg-text-muted',
    failed: 'bg-danger-500',
    sleeping: 'bg-purple-400',
    executing: 'bg-primary-400',
    accomplished: 'bg-success-500',
    queued: 'bg-text-muted',
    malfunction: 'bg-danger-500',
    standby: 'bg-purple-400',
  };

  return (
    <span
      className={cn(
        baseStyles,
        variantStyles[variant],
        pulse && 'animate-pulse',
        className
      )}
    >
      {dot && (
        <span
          className={cn(
            'w-1.5 h-1.5 rounded-full',
            dotColors[variant],
            pulse && 'animate-ping'
          )}
        />
      )}
      {children}
    </span>
  );
}
